import { Badge } from "@/components/ui/badge";
import { CustomerTile } from "@/components/molecules/customer-tile";
import { FormTile } from "@/components/molecules/form-tile";
import type { Qualification } from "@/features/qualifications/types";
import { statusTone } from "@/features/utils/colors";
import { isAwaitingScore } from "@/features/qualifications/utils/score-calculator";
import { CircleNotchIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

export const useQualificationsColumns = (): ColumnDef<Qualification>[] => {
  const { t } = useTranslation("qualifications");
  const { format } = useDateFormat();
  return [
    {
      accessorKey: "customer_id",
      header: t("submissions.columns.customer"),
      cell: ({ row }) => {
        const customerId = row.original.customer_id;
        return customerId ? (
          <CustomerTile
            customerId={customerId}
            fallback={t("submissions.columns.unknown_customer")}
            clickable
          />
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: "score",
      header: t("submissions.columns.score"),
      cell: ({ row }) => {
        const score = row.original.score;
        const submittedAt = row.original.submitted_at;

        // Created when the call starts, scored when it is wrapped up
        if (isAwaitingScore(row.original)) {
          return (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <CircleNotchIcon
                className="size-4 animate-spin"
                aria-label={t("submissions.view.scoring")}
              />
            </div>
          );
        }

        // Don't show score if not yet submitted for scoring
        if (!submittedAt || score === null || score === undefined) {
          return (
            <div className="flex items-center justify-center h-full">
              <Badge variant="outline" className="text-xs">
                {t("submissions.statuses.pending")}
              </Badge>
            </div>
          );
        }

        const percentage = Math.round(score * 100);
        // The same three bands as a status: good, to look at, poor.
        const tone =
          score >= 0.8
            ? "bg-success-soft text-success-foreground"
            : score < 0.5
              ? "bg-danger-soft text-danger-foreground"
              : "bg-warning-soft text-warning-foreground";

        return (
          <div
            className={`flex items-center justify-center h-full -m-3 p-3 border-l ${tone}`}
          >
            <span className="font-semibold text-lg">{percentage}%</span>
          </div>
        );
      },
    },
    {
      accessorKey: "form_id",
      header: t("submissions.columns.form"),
      cell: ({ row }) => {
        const formId = row.original.form_id;
        return formId ? (
          <FormTile formId={formId} />
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("submissions.columns.status"),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge color={statusTone(status) || "gray"} variant="outline">
            {t(
              `submissions.statuses.${status}`,
              status.charAt(0).toUpperCase() + status.slice(1),
            )}
          </Badge>
        );
      },
    },
    {
      accessorKey: "completed",
      header: t("submissions.columns.completed"),
      cell: ({ row }) => {
        const completed = row.original.completed;
        return completed ? (
          <Badge variant="outline" color="green">
            {t("submissions.completion.complete")}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">
            {t("submissions.completion.incomplete")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "reviewed_at",
      header: t("submissions.columns.review_mode"),
      cell: ({ row }) => {
        const reviewedAt = row.original.reviewed_at;
        const status = row.original.status;
        const isAuto = status !== "pending" && !reviewedAt;
        return isAuto ? (
          <Badge variant="outline" className="text-xs">
            {t("submissions.review_mode.auto")}
          </Badge>
        ) : reviewedAt ? (
          <Badge variant="outline" className="text-xs">
            {t("submissions.review_mode.manual")}
          </Badge>
        ) : null;
      },
    },
    {
      accessorKey: "submitted_at",
      header: t("submissions.columns.submitted"),
      cell: ({ row }) => {
        const submittedAt = row.original.submitted_at;
        if (!submittedAt)
          return <span className="text-muted-foreground text-sm">-</span>;
        return (
          <span className="text-sm text-muted-foreground">
            {format(submittedAt, "PP")}
          </span>
        );
      },
    },
  ];
};
